import React, { useState, useEffect, useCallback } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaUserTie,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaEnvelope,
  FaSpinner,
} from "react-icons/fa";
import apiRequest from "../services/api";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");

  const emptyForm = {
    id: null,
    tin: "",
    pen: "",
    idno: "",
    fullName: "",
    phone: "",
    email: "",
    hireDate: "",
    bank: "",
    account: "",
    salary: 0.0,
    departmentId: "",
    positionId: "",
    salaryStructureId: "",
    type: "FULLTIME",
    status: "ACTIVE",
  };

  const [formData, setFormData] = useState(emptyForm);

  /* =========================================================
     LOOKUPS
  ========================================================= */

  const fetchFormLookups = async () => {
    try {
      const [deptResp, posResp, strResp] = await Promise.all([
        apiRequest("employees/departments"),
        apiRequest("employees/positions"),
        apiRequest("payrolls/structures"),
      ]);

      setDepartments(deptResp.data || []);
      setPositions(posResp.data || []);
      setSalaryStructures(strResp.data || []);
    } catch (err) {
      console.error("Failed to populate relational lookups:", err);
    }
  };

  /* =========================================================
     EMPLOYEE QUERY
  ========================================================= */

  const fetchEmployees = useCallback(
    async (page = 0, keyword = "") => {
      setIsLoading(true);
      setError("");

      try {
        const endpoint = `employees?keyword=${encodeURIComponent(
          keyword
        )}&page=${page}&size=${pagination.pageSize}`;

        const resp = await apiRequest(endpoint);
        const pageData = resp.data;

        setEmployees(pageData?.content || []);

        setPagination((prev) => ({
          ...prev,
          currentPage: pageData?.number ?? page,
          totalPages: pageData?.totalPages ?? 0,
          totalElements: pageData?.totalElements ?? 0,
        }));
      } catch (err) {
        setError(err?.message || "Failed to search employee records.");
        setEmployees([]);
      } finally {
        setIsLoading(false);
      }
    },
    [pagination.pageSize]
  );

  useEffect(() => {
    fetchEmployees(0, "");
    fetchFormLookups();
  }, [fetchEmployees]);

  /* =========================================================
     SEARCH
  ========================================================= */

  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchKeyword(searchInput.trim());
      setPagination((prev) => ({
        ...prev,
        currentPage: 0,
      }));

      fetchEmployees(0, searchInput.trim());
    }, 400);

    return () => clearTimeout(delay);
  }, [searchInput]);

  /* =========================================================
     MODAL
  ========================================================= */

  const openModal = (mode, employee = null) => {
    setModalMode(mode);
    setError("");

    if (employee) {
      setFormData({
        id: employee.id,
        tin: employee.tin || "",
        pen: employee.pen || "",
        idno: employee.idno || "",
        fullName: employee.fullName || "",
        phone: employee.phone || "",
        email: employee.email || "",
        hireDate: employee.hireDate || "",
        bank: employee.bank || "",
        account: employee.account || "",
        salary: employee.salary || 0.0,
        type: employee.type || "FULLTIME",
        status: employee.status || "ACTIVE",
        departmentId: employee.department?.id || employee.departmentId || "",
        positionId: employee.position?.id || employee.positionId || "",
        salaryStructureId:
          employee.salaryStructure?.id || employee.salaryStructureId || "",
      });
    } else {
      setFormData(emptyForm);
    }

    setIsModalOpen(true);
  };

  /* =========================================================
     SAVE
  ========================================================= */

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (
      !formData.fullName.trim() ||
      !formData.phone.trim() ||
      !formData.tin.trim()
    ) {
      setError("Full Name, Phone number, and TIN are required.");
      return;
    }

    const endpoint = "employees";
    const method = modalMode === "create" ? "POST" : "PUT";

    const payload = {
      id: formData.id,
      tin: formData.tin.trim(),
      pen: formData.pen.trim(),
      idno: formData.idno,
      fullName: formData.fullName.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      hireDate: formData.hireDate || null,
      bank: formData.bank.trim(),
      account: formData.account.trim(),
      salary: parseFloat(formData.salary) || 0.0,
      type: formData.type,
      status: formData.status,

      department: formData.departmentId
        ? { id: parseInt(formData.departmentId, 10) }
        : null,

      position: formData.positionId
        ? { id: parseInt(formData.positionId, 10) }
        : null,

      salaryStructure: formData.salaryStructureId
        ? { id: parseInt(formData.salaryStructureId, 10) }
        : null,
    };

    try {
      await apiRequest(endpoint, {
        method,
        body: payload,
      });

      await fetchEmployees(pagination.currentPage, searchKeyword);

      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Could not save employee data.");
    }
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete = async (id, name) => {
    if (
      !window.confirm(`Are you sure you want to remove employee "${name}"?`)
    ) {
      return;
    }

    try {
      await apiRequest(`employees/${id}`, {
        method: "DELETE",
      });

      fetchEmployees(pagination.currentPage, searchKeyword);
    } catch (err) {
      setError(err?.message || "Failed to delete the selected profile.");
    }
  };

  /* =========================================================
     STYLES
  ========================================================= */

  const inputClass = `
    w-full rounded-xl px-3 py-2.5 text-xs transition
    focus:outline-none focus:border-emerald-500/40
    bg-slate-50 border border-slate-200 text-slate-800
    placeholder-slate-400
    dark:bg-slate-950/60 dark:border-white/10
    dark:text-white dark:placeholder-slate-600
    disabled:opacity-60 disabled:cursor-not-allowed
  `;

  const labelClass =
    "text-[10px] font-bold tracking-wider uppercase text-slate-500 dark:text-slate-400";

  return (
    <div
      className="
        min-h-full p-4 md:p-8 font-sans
        bg-slate-50 text-slate-800
        dark:bg-slate-950 dark:text-slate-100
      "
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* =====================================================
            HEADER
        ===================================================== */}

        <div
          className="
            flex flex-col md:flex-row md:items-center
            justify-between gap-4 p-5 rounded-2xl
            shadow-sm border backdrop-blur-md
            bg-white border-slate-200
            dark:bg-slate-900/40 dark:border-white/10
            dark:shadow-xl
          "
        >
          <div className="flex items-center gap-3.5">
            <div
              className="
                p-3 rounded-xl shadow-inner border
                bg-gradient-to-br from-emerald-100 to-teal-50
                border-emerald-200 text-emerald-600
                dark:from-emerald-500/20 dark:to-teal-500/10
                dark:border-emerald-500/30 dark:text-emerald-400
              "
            >
              <FaUserTie className="text-2xl" />
            </div>

            <div>
              <h1
                className="
                  text-xl font-black tracking-tight uppercase
                  text-slate-900 dark:text-white
                "
              >
                Employee Management
              </h1>

              <p className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                  {pagination.totalElements}
                </span>{" "}
                employee profiles registered
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => openModal("create")}
            className="
              flex items-center justify-center gap-2
              px-4 py-2.5 rounded-xl text-xs font-bold
              transition shadow-sm
              bg-emerald-600 hover:bg-emerald-500
              text-white
            "
          >
            <FaPlus />
            New Employee
          </button>
        </div>

        {/* =====================================================
            SEARCH
        ===================================================== */}

        <div
          className="
            grid grid-cols-1 gap-3 p-3 rounded-xl border
            backdrop-blur-sm
            bg-white border-slate-200
            dark:bg-slate-900/20 dark:border-white/5
          "
        >
          <div className="relative">
            <span
              className="
                absolute inset-y-0 left-0 pl-3
                flex items-center pointer-events-none
                text-slate-400 dark:text-slate-500
              "
            >
              <FaSearch className="text-xs" />
            </span>

            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by employee name, TIN, PEN, phone or ID..."
              className="
                w-full rounded-xl pl-9 pr-4 py-2.5 text-xs
                transition focus:outline-none
                focus:border-emerald-500/40
                bg-slate-50 border border-slate-200
                text-slate-800 placeholder-slate-400
                dark:bg-slate-950/60 dark:border-white/10
                dark:text-white dark:placeholder-slate-600
              "
            />
          </div>
        </div>

        {/* =====================================================
            ERROR
        ===================================================== */}

        {error && (
          <div
            className="
              p-4 rounded-xl border text-xs
              bg-red-50 border-red-200 text-red-700
              dark:bg-red-500/10 dark:border-red-500/20
              dark:text-red-400
            "
          >
            {error}
          </div>
        )}

        {/* =====================================================
            TABLE
        ===================================================== */}

        <div
          className="
            border rounded-2xl shadow-sm backdrop-blur-md
            bg-white border-slate-200
            dark:bg-slate-900/30 dark:border-white/10
            dark:shadow-2xl
          "
        >
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[1050px]">
              <thead>
                <tr
                  className="
                    border-b text-[10px] font-bold
                    tracking-wider uppercase
                    bg-slate-50 border-slate-200 text-slate-500
                    dark:bg-slate-950/50 dark:border-white/10
                    dark:text-slate-400
                  "
                >
                  <th className="px-5 py-4">Employee</th>

                  <th className="px-4 py-4">Department</th>

                  <th className="px-4 py-4">Status / Type</th>

                  <th className="px-4 py-4">Contact</th>

                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody
                className="
                  divide-y text-xs
                  divide-slate-100 text-slate-600
                  dark:divide-white/5 dark:text-slate-300
                "
              >
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="
                        text-center py-14
                        text-slate-500
                        dark:text-slate-500
                      "
                    >
                      <div className="flex items-center justify-center gap-2">
                        <FaSpinner className="animate-spin text-emerald-500" />
                        Loading employee records...
                      </div>
                    </td>
                  </tr>
                ) : employees.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="
                        text-center py-14 italic
                        text-slate-500
                        dark:text-slate-500
                      "
                    >
                      No employee records found.
                    </td>
                  </tr>
                ) : (
                  employees.map((emp) => (
                    <tr
                      key={emp.id}
                      className="
                        group transition
                        hover:bg-slate-50
                        dark:hover:bg-white/[0.02]
                      "
                    >
                      {/* EMPLOYEE */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="
                              w-9 h-9 rounded-lg
                              flex items-center justify-center
                              border
                              bg-emerald-50 border-emerald-100
                              text-emerald-600
                              dark:bg-emerald-500/10
                              dark:border-emerald-500/20
                              dark:text-emerald-400
                            "
                          >
                            <FaUserTie className="text-xs" />
                          </div>

                          <div>
                            <div
                              className="
                                font-semibold
                                text-slate-900
                                dark:text-white
                              "
                            >
                              {emp.fullName}
                            </div>

                            <div
                              className="
                                text-[10px] mt-0.5
                                text-slate-500
                                dark:text-slate-500
                              "
                            >
                              ID: {emp.idno || "-"} · TIN: {emp.tin || "-"} ·
                              PEN: {emp.pen || "-"}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* DEPARTMENT */}
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <span
                            className="
                              inline-flex px-2 py-0.5 rounded
                              text-[10px] font-semibold
                              border
                              bg-emerald-50
                              text-emerald-700
                              border-emerald-200
                              dark:bg-emerald-500/10
                              dark:text-emerald-400
                              dark:border-emerald-500/20
                            "
                          >
                            {emp.departmentName ||
                              emp.department?.name ||
                              "Unassigned"}
                          </span>

                          <div
                            className="
                              text-[11px]
                              text-slate-500
                              dark:text-slate-400
                            "
                          >
                            {emp.positionName ||
                              emp.position?.name ||
                              "No Position Assigned"}
                          </div>
                        </div>
                      </td>

                      {/* STATUS */}
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase border ${
                              emp.status === "ACTIVE"
                                ? `
                                  bg-emerald-50 text-emerald-700
                                  border-emerald-200
                                  dark:bg-emerald-500/10
                                  dark:text-emerald-400
                                  dark:border-emerald-500/20
                                `
                                : `
                                  bg-amber-50 text-amber-700
                                  border-amber-200
                                  dark:bg-amber-500/10
                                  dark:text-amber-400
                                  dark:border-amber-500/20
                                `
                            }`}
                          >
                            {emp.status || "ACTIVE"}
                          </span>

                          <div
                            className="
                              text-[10px] font-mono
                              text-slate-500
                              dark:text-slate-400
                            "
                          >
                            {emp.type || "FULLTIME"}
                          </div>
                        </div>
                      </td>

                      {/* CONTACT */}
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          {emp.email && (
                            <div className="flex items-center gap-1.5">
                              <FaEnvelope
                                className="
                                  text-[9px]
                                  text-slate-400
                                  dark:text-slate-500
                                "
                              />

                              <span
                                className="
                                  text-slate-700
                                  dark:text-slate-300
                                "
                              >
                                {emp.email}
                              </span>
                            </div>
                          )}

                          {emp.phone && (
                            <div
                              className="
                                text-[11px] pl-4
                                text-slate-500
                                dark:text-slate-400
                              "
                            >
                              {emp.phone}
                            </div>
                          )}

                          {!emp.email && !emp.phone && (
                            <span className="text-slate-400">-</span>
                          )}
                        </div>
                      </td>

                      {/* ACTIONS */}
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => openModal("view", emp)}
                            title="View Profile"
                            className="
                              p-2 rounded-lg border
                              transition
                              bg-slate-50 border-slate-200
                              text-slate-500
                              hover:bg-slate-100
                              dark:bg-white/5
                              dark:border-white/10
                              dark:text-slate-300
                              dark:hover:bg-white/10
                            "
                          >
                            <FaEye className="text-xs" />
                          </button>

                          <button
                            type="button"
                            onClick={() => openModal("edit", emp)}
                            title="Edit Employee"
                            className="
                              p-2 rounded-lg border transition
                              bg-emerald-50
                              border-emerald-100
                              text-emerald-600
                              hover:bg-emerald-100
                              dark:bg-emerald-500/10
                              dark:border-emerald-500/20
                              dark:text-emerald-400
                              dark:hover:bg-emerald-500/20
                            "
                          >
                            <FaEdit className="text-xs" />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(emp.id, emp.fullName)}
                            title="Delete Employee"
                            className="
                              p-2 rounded-lg border transition
                              bg-red-50 border-red-100
                              text-red-600
                              hover:bg-red-100
                              dark:bg-red-500/10
                              dark:border-red-500/20
                              dark:text-red-400
                              dark:hover:bg-red-500/20
                            "
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ===================================================
              TABLE LOADING
          =================================================== */}

          {isLoading && employees.length > 0 && (
            <div
              className="
                p-4 flex items-center justify-center gap-2
                text-xs border-t
                bg-slate-50 text-slate-500 border-slate-200
                dark:bg-slate-950/20
                dark:text-slate-400
                dark:border-white/5
              "
            >
              <FaSpinner className="animate-spin text-emerald-500" />
              Updating employee records...
            </div>
          )}
        </div>

        {/* =====================================================
            PAGINATION
        ===================================================== */}

        <div
          className="
            flex flex-col sm:flex-row
            sm:items-center justify-between
            gap-3 p-3 rounded-xl border
            bg-white border-slate-200
            dark:bg-slate-900/20 dark:border-white/5
          "
        >
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Showing{" "}
            <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
              {employees.length}
            </span>{" "}
            of{" "}
            <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
              {pagination.totalElements}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pagination.currentPage === 0 || isLoading}
              onClick={() =>
                fetchEmployees(pagination.currentPage - 1, searchKeyword)
              }
              className="
                p-2 rounded-lg border transition
                disabled:opacity-40
                bg-slate-50 border-slate-200
                text-slate-600 hover:bg-slate-100
                dark:bg-white/5 dark:border-white/10
                dark:text-slate-300
                dark:hover:bg-white/10
              "
            >
              <FaChevronLeft className="text-xs" />
            </button>

            <span
              className="
                text-xs font-mono
                text-slate-600
                dark:text-slate-300
              "
            >
              Page {pagination.currentPage + 1} / {pagination.totalPages || 1}
            </span>

            <button
              type="button"
              disabled={
                pagination.currentPage + 1 >= pagination.totalPages || isLoading
              }
              onClick={() =>
                fetchEmployees(pagination.currentPage + 1, searchKeyword)
              }
              className="
                p-2 rounded-lg border transition
                disabled:opacity-40
                bg-slate-50 border-slate-200
                text-slate-600 hover:bg-slate-100
                dark:bg-white/5 dark:border-white/10
                dark:text-slate-300
                dark:hover:bg-white/10
              "
            >
              <FaChevronRight className="text-xs" />
            </button>
          </div>

          <select
            value={pagination.pageSize}
            onChange={(e) => {
              setPagination((prev) => ({
                ...prev,
                pageSize: Number(e.target.value),
                currentPage: 0,
              }));
            }}
            className="
              rounded-lg px-3 py-2 text-xs border font-semibold
              bg-slate-50 border-slate-200 text-slate-800
              dark:bg-slate-950/60
              dark:border-white/10
              dark:text-white
            "
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* =======================================================
          EMPLOYEE MODAL
      ======================================================= */}

      {isModalOpen && (
        <div
          className="
            fixed inset-0 z-50
            flex items-center justify-center p-4
            bg-slate-950/50 dark:bg-slate-950/75
            backdrop-blur-sm
          "
        >
          <div
            className="
              w-full max-w-3xl
              max-h-[92vh] overflow-y-auto
              rounded-2xl shadow-2xl border
              bg-white border-slate-200
              dark:bg-slate-900
              dark:border-white/10
            "
          >
            {/* MODAL HEADER */}

            <div
              className="
                sticky top-0 z-10
                flex justify-between items-center
                px-6 py-4 border-b backdrop-blur-md
                bg-white/95 border-slate-200
                dark:bg-slate-900/95
                dark:border-white/10
              "
            >
              <div className="flex items-center gap-3">
                <div
                  className="
                    p-2.5 rounded-xl border
                    bg-emerald-50
                    border-emerald-200
                    text-emerald-600
                    dark:bg-emerald-500/10
                    dark:border-emerald-500/20
                    dark:text-emerald-400
                  "
                >
                  <FaUserTie />
                </div>

                <div>
                  <h3
                    className="
                      text-base font-black uppercase
                      text-slate-900
                      dark:text-white
                    "
                  >
                    {modalMode} Employee
                  </h3>

                  <p
                    className="
                      text-[10px] mt-0.5
                      text-slate-500
                      dark:text-slate-400
                    "
                  >
                    {modalMode === "view"
                      ? "Read-only employee profile."
                      : "Enter employee and payroll information."}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="
                  p-2 rounded-lg transition
                  text-slate-400 hover:text-slate-700
                  hover:bg-slate-100
                  dark:text-slate-500
                  dark:hover:text-white
                  dark:hover:bg-white/10
                "
              >
                <FaTimes />
              </button>
            </div>

            {/* FORM */}

            <form onSubmit={handleSave} className="p-6 space-y-6">
              {/* PERSONAL */}

              <section className="space-y-4">
                <div
                  className="
                    flex items-center gap-2
                    pb-2 border-b
                    border-slate-200
                    dark:border-white/10
                  "
                >
                  <div className="w-1 h-4 rounded-full bg-emerald-500" />

                  <h4
                    className="
                      text-[10px] font-bold
                      tracking-wider uppercase
                      text-slate-700
                      dark:text-slate-300
                    "
                  >
                    Personal Information
                  </h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className={labelClass}>Full Name *</label>

                    <input
                      type="text"
                      required
                      disabled={modalMode === "view"}
                      value={formData.fullName || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          fullName: e.target.value,
                        })
                      }
                      placeholder="e.g. John Doe"
                      className={inputClass}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>Employee ID</label>

                    <input
                      type="text"
                      disabled
                      value={formData.idno || "Auto-Generated"}
                      className={`${inputClass} opacity-70`}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>
                      Tax Identification Number *
                    </label>

                    <input
                      type="text"
                      required
                      disabled={modalMode === "view"}
                      value={formData.tin || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          tin: e.target.value,
                        })
                      }
                      placeholder="0012345678"
                      className={inputClass}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>Pension Number</label>

                    <input
                      type="text"
                      disabled={modalMode === "view"}
                      value={formData.pen || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pen: e.target.value,
                        })
                      }
                      placeholder="Pension number"
                      className={inputClass}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>Phone Number *</label>

                    <input
                      type="text"
                      required
                      disabled={modalMode === "view"}
                      value={formData.phone || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          phone: e.target.value,
                        })
                      }
                      placeholder="09..."
                      className={inputClass}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>Email Address</label>

                    <input
                      type="email"
                      disabled={modalMode === "view"}
                      value={formData.email || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          email: e.target.value,
                        })
                      }
                      placeholder="name@company.com"
                      className={inputClass}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>Hire Date</label>

                    <input
                      type="date"
                      disabled={modalMode === "view"}
                      value={formData.hireDate || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          hireDate: e.target.value,
                        })
                      }
                      className={inputClass}
                    />
                  </div>
                </div>
              </section>

              {/* EMPLOYMENT */}

              <section className="space-y-4">
                <div
                  className="
                    flex items-center gap-2
                    pb-2 border-b
                    border-slate-200
                    dark:border-white/10
                  "
                >
                  <div className="w-1 h-4 rounded-full bg-emerald-500" />

                  <h4
                    className="
                      text-[10px] font-bold
                      tracking-wider uppercase
                      text-slate-700
                      dark:text-slate-300
                    "
                  >
                    Employment
                  </h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className={labelClass}>Employment Type</label>

                    <select
                      disabled={modalMode === "view"}
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          type: e.target.value,
                        })
                      }
                      className={inputClass}
                    >
                      <option value="FULLTIME">Full-Time</option>
                      <option value="CONTRACT">Contract</option>
                      <option value="INTERSHIP">Intern</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>Status</label>

                    <select
                      disabled={modalMode === "view"}
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value,
                        })
                      }
                      className={inputClass}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Suspended</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* PAYROLL */}

              <section className="space-y-4">
                <div
                  className="
                    flex items-center gap-2
                    pb-2 border-b
                    border-slate-200
                    dark:border-white/10
                  "
                >
                  <div className="w-1 h-4 rounded-full bg-emerald-500" />

                  <h4
                    className="
                      text-[10px] font-bold
                      tracking-wider uppercase
                      text-slate-700
                      dark:text-slate-300
                    "
                  >
                    Payroll & Banking
                  </h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <label className={labelClass}>Bank</label>

                    <input
                      type="text"
                      disabled={modalMode === "view"}
                      value={formData.bank || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bank: e.target.value,
                        })
                      }
                      placeholder="CBE"
                      className={inputClass}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>Account Number</label>

                    <input
                      type="text"
                      disabled={modalMode === "view"}
                      value={formData.account || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          account: e.target.value,
                        })
                      }
                      placeholder="10000..."
                      className={inputClass}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>Base Salary</label>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      disabled={modalMode === "view"}
                      value={formData.salary ?? ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          salary: e.target.value,
                        })
                      }
                      placeholder="0.00"
                      className={inputClass}
                    />
                  </div>
                </div>
              </section>

              {/* ORGANIZATION */}

              <section className="space-y-4">
                <div
                  className="
                    flex items-center gap-2
                    pb-2 border-b
                    border-slate-200
                    dark:border-white/10
                  "
                >
                  <div className="w-1 h-4 rounded-full bg-emerald-500" />

                  <h4
                    className="
                      text-[10px] font-bold
                      tracking-wider uppercase
                      text-slate-700
                      dark:text-slate-300
                    "
                  >
                    Organization
                  </h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <label className={labelClass}>Department</label>

                    <select
                      disabled={modalMode === "view"}
                      value={formData.departmentId || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          departmentId: e.target.value
                            ? parseInt(e.target.value, 10)
                            : "",
                        })
                      }
                      className={inputClass}
                    >
                      <option value="">Select Department...</option>

                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>Job Position</label>

                    <select
                      disabled={modalMode === "view"}
                      value={formData.positionId || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          positionId: e.target.value
                            ? parseInt(e.target.value, 10)
                            : "",
                        })
                      }
                      className={inputClass}
                    >
                      <option value="">Select Position...</option>

                      {positions.map((pos) => (
                        <option key={pos.id} value={pos.id}>
                          {pos.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className={labelClass}>Salary Structure</label>

                    <select
                      disabled={modalMode === "view"}
                      value={formData.salaryStructureId || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          salaryStructureId: e.target.value
                            ? parseInt(e.target.value, 10)
                            : "",
                        })
                      }
                      className={inputClass}
                    >
                      <option value="">Select Structure...</option>

                      {salaryStructures.map((struct) => (
                        <option key={struct.id} value={struct.id}>
                          {struct.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              {/* FOOTER */}

              <div
                className="
                  flex justify-end gap-3 pt-4 border-t
                  border-slate-200
                  dark:border-white/10
                "
              >
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="
                    px-4 py-2.5 rounded-xl text-xs
                    font-semibold transition
                    bg-slate-100 text-slate-600
                    hover:bg-slate-200
                    dark:bg-white/5
                    dark:text-slate-300
                    dark:hover:bg-white/10
                  "
                >
                  Cancel
                </button>

                {modalMode !== "view" && (
                  <button
                    type="submit"
                    className="
                      px-5 py-2.5 rounded-xl
                      bg-emerald-600
                      hover:bg-emerald-500
                      text-white text-xs
                      font-semibold transition
                      shadow-sm
                    "
                  >
                    {modalMode === "create" ? "Save Employee" : "Save Changes"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;
